import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';

@Injectable({ providedIn: 'root' })
export class GameSettingsStorageService {
  private fallbackImage = '/assets/images/image-not-found-150x150.png';

  getValue<T>(key: string, parseJson = false): T | null {
    const value = localStorage.getItem(key);
    if (!value) return null;
    return parseJson ? JSON.parse(value) : (value as any as T);
  }

  applyGameSettings(
    component: any, // parent component reference
    form: FormGroup,
    verticalStepperStep5: FormGroup,
    prizeButtons: any[]
  ) {
    // Helper function to patch validators safely
    const patchControl = (controlName: string) => {
      const control = verticalStepperStep5.get(controlName);
      if (control) {
        control.clearValidators();
        control.updateValueAndValidity();
      }
    };

    // Grid type
    const gridType = this.getValue<string>('gridType');
    if (gridType) component.gridType = gridType;

    // Pop-up
    const popUp = this.getValue<any>('popUp', true);
    if (popUp) component.tabForm.patchValue({ popUp });

    // Game logo
    const gameLogo = this.getValue<string>('gameLogo');
    if (gameLogo) {
      component.gameLogo = gameLogo;
      form.patchValue({ logo: gameLogo });

      const logoStretch = this.getValue<boolean>('logoStretch', true);
      if (logoStretch !== null) component.logoStretch = logoStretch;

      patchControl('logo');
    }

    const gameLogoUrl = this.getValue<string>('gameLogoUrl');
    if (gameLogoUrl) component.gameLogoUrl = gameLogoUrl;

    // Menu art
    const menuArt = this.getValue<string>('menuArt');
    if (menuArt) {
      component.menuArt = menuArt;
      form.patchValue({ gameMenuArt: menuArt });

      const imageScale = this.getValue<number>('imageScale', true);
      if (imageScale !== null) component.imageScale = imageScale;

      patchControl('gameMenuArt');
    }

    const menuArtUrl = this.getValue<string>('menuArtUrl');
    if (menuArtUrl) component.menuArtUrl = menuArtUrl;

    // Layer (new addition)
    const layer = this.getValue<string>('layer');
    if (layer) {
      component.layer = layer;
      form.patchValue({ gameLayer: layer });

      const layerScale = this.getValue<number>('layerScale', true);
      if (layerScale !== null) component.layerScale = layerScale;

      patchControl('gameLayer');
    }

    const layerUrl = this.getValue<string>('layerUrl');
    if (layerUrl) component.layerUrl = layerUrl;

    // Other fields
    const selectedPlayers = this.getValue<any[]>('selectedPlayers', true);
    if (selectedPlayers) component.game.selectedPlayers = selectedPlayers;

    const invites = this.getValue<any[]>('invites', true);
    if (invites) {
      component.invitesData.invites =
        invites.length > 0 ? invites : component.gameSettingsService.initializeInvites();
    }

    const powerBar = this.getValue<any>('powerBar', true);
    if (powerBar) component.invitesData.powerBar = powerBar;

    const prizeMode = this.getValue<string>('prizeMode');
    if (prizeMode) {
      component.game.prizeMode = prizeMode;

      if (prizeMode === 'auto select mode') component.createPrizeForm(1);
      else {
        const prizeButton = prizeButtons.find((el) => el.type === prizeMode);
        if (prizeButton) component.createPrizeForm(prizeButton.numOfPrizes);
      }

      const prize = this.getValue<any>('prize', true);
      if (prize) {
        const prizeForm = this.getValue<any>('prizeForm', true);
        if (prizeForm) component.prizeForm.patchValue({ prize: prizeForm });
        component.settingPrizesFormCookies(prize);
      }
    }

    const inviteType = this.getValue<string>('inviteType');
    if (inviteType) component.inviteType = inviteType;

    const isInviteGradient = this.getValue<boolean>('isInviteImageArtGradient', true);
    if (isInviteGradient !== null)
      component.invitesData.isInviteImageArtGradient = isInviteGradient;

    const isGameMenuToolbar = this.getValue<boolean>('isGameMenuToolbar', true);
    if (isGameMenuToolbar !== null)
      component.gameSettingsService.isGameMenuToolbar = Boolean(isGameMenuToolbar);

    const step5 = this.getValue<any>('step5', true);
    if (step5) {
      component.menuArt1stColor = step5.menuArt1stColor || '#efeeee';
      component.menuArt2ndColor = step5.menuArt2ndColor || '#efeeee';
    }
  }
}
